import React, { useState } from "react";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { LiveChip } from "./views/_shared";
import { eviqScore } from "../../lib/batteryAnalytics";

/**
 * EviqScoreCard — headline composite score on the Home tab (and mirrored
 * onto the public Battery Passport). Reads like a credit score: one
 * number, one letter grade, five weighted components.
 *
 * The score is intentionally deterministic (no LLM, no stochastic input)
 * so the same pack + snapshot always produces the same score — which is
 * what lets us bake it into the signed passport payload without breaking
 * the HMAC.
 */
export const EviqScoreCard = ({ vehicle, climate }) => {
  const [open, setOpen] = useState(false);
  const { total, grade, breakdown } = eviqScore(vehicle, climate);

  // Color the tile by grade — same neon-green scale as the rest of the
  // Home view so the score visually anchors the page.
  const tone =
    total >= 90 ? "#39FF14"
      : total >= 80 ? "#7FD1FF"
      : total >= 70 ? "#00E5FF"
      : total >= 60 ? "#FFB020"
      : "#FF6B57";

  return (
    <div
      className="rounded-2xl border p-4 relative"
      style={{
        borderColor: `${tone}55`,
        background: `linear-gradient(135deg, ${tone}10 0%, rgba(255,255,255,0.02) 100%)`,
      }}
      data-testid="eviq-score-card"
    >
      {vehicle?.live && (
        <div className="absolute top-2 right-2">
          <LiveChip testid="eviq-score-live" />
        </div>
      )}

      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0"
          style={{ background: `${tone}18`, borderColor: `${tone}55` }}
        >
          <Sparkles className="w-5 h-5" style={{ color: tone }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            EVIQ Score
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <div
              className="font-display text-4xl font-semibold leading-none"
              style={{ color: tone }}
              data-testid="eviq-score-total"
            >
              {total}
            </div>
            <div
              className="font-mono text-[11px] uppercase tracking-wider"
              style={{ color: tone }}
              data-testid="eviq-score-grade"
            >
              {grade}
            </div>
          </div>
          <div className="font-mono text-[10px] text-zinc-500 leading-snug mt-1">
            Composite of SoH, degradation trend, charging quality,
            thermal stress &amp; fleet rank.
          </div>
        </div>
      </div>

      {/* Component breakdown — expandable so the tile stays compact */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-3 w-full flex items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
        aria-expanded={open}
        data-testid="eviq-score-toggle"
      >
        <span>{open ? "Hide" : "Show"} score breakdown</span>
        {open ? <ChevronUp className="w-3 h-3" strokeWidth={2.4} /> : <ChevronDown className="w-3 h-3" strokeWidth={2.4} />}
      </button>

      {open && (
        <div className="mt-3 space-y-2" data-testid="eviq-score-breakdown">
          {breakdown.map((row) => {
            const pct = Math.max(0, Math.min(100, (row.pts / row.max) * 100));
            return (
              <div key={row.label} data-testid={`eviq-score-row-${row.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[11px] text-zinc-300">{row.label}</span>
                  <span className="font-mono text-[10px] text-zinc-400">
                    {row.pts} / {row.max}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: tone }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
